import { 
  Category,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  DeleteCategoryRequest,
  CategoriesResponse,
  RecentCategoriesResponse
} from '@/types/category';

// Frontend API functions for category management
export async function fetchCategories(coupleId: string, includeInactive = false): Promise<CategoriesResponse> {
  try {
    const params = new URLSearchParams({
      couple_id: coupleId,
      include_inactive: includeInactive.toString()
    });

    const response = await fetch(`/.netlify/functions/getCategories?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch categories');
    }

    return result.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
}

export async function fetchRecentCategories(coupleId: string, limit = 5): Promise<RecentCategoriesResponse> {
  try {
    const params = new URLSearchParams({
      couple_id: coupleId,
      limit: limit.toString()
    });

    const response = await fetch(`/.netlify/functions/getRecentCategories?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch recent categories');
    }

    return result.data;
  } catch (error) {
    console.error('Error fetching recent categories:', error);
    throw error;
  }
}

export async function createCategory(data: CreateCategoryRequest): Promise<Category> {
  try {
    const response = await fetch('/.netlify/functions/addCategory', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to create category');
    }

    return result.data;
  } catch (error) {
    console.error('Error creating category:', error);
    throw error;
  }
}

export async function updateCategory(data: UpdateCategoryRequest): Promise<Category> {
  try {
    const response = await fetch('/.netlify/functions/updateCategory', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to update category');
    }

    return result.data;
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
}

export async function deleteCategory(data: DeleteCategoryRequest): Promise<void> {
  try {
    const response = await fetch('/.netlify/functions/deleteCategory', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to delete category');
    }
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
}

// Helper function to get category by ID
export function getCategoryById(categories: Category[], categoryId: string): Category | undefined {
  return categories.find(category => category.id === categoryId);
}

// Helper function to sort categories
export function sortCategories(categories: Category[], sortBy: 'name' | 'usage_count' | 'created_at' = 'name', direction: 'asc' | 'desc' = 'asc'): Category[] {
  return [...categories].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'usage_count':
        comparison = a.usage_count - b.usage_count;
        break;
      default:
        comparison = a.name.localeCompare(b.name);
    }
    
    return direction === 'desc' ? -comparison : comparison;
  });
}

// Helper function to filter categories
export function filterCategories(categories: Category[], searchTerm?: string, includeInactive = false): Category[] {
  let filtered = categories;
  
  if (!includeInactive) {
    filtered = filtered.filter(category => category.is_active);
  }
  
  if (searchTerm && searchTerm.trim().length > 0) {
    const term = searchTerm.trim().toLowerCase();
    filtered = filtered.filter(category => 
      category.name.toLowerCase().includes(term)
    );
  }
  
  return filtered;
}